import { motion } from "framer-motion";
import { Sparkles, Cpu, LineChart, Layers, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/data";
import BgLayer from "@/components/ui-shared/BgLayer";

const cards = [
    {
        title: "Xelerate Copilot",
        desc: "An always-on AI mentor that meets every learner where they are — surfacing the next best action, coaching in real time.",
        icon: Sparkles,
    },
    {
        title: "Skills Graph Intelligence",
        desc: "An enterprise-tuned taxonomy that continuously infers proficiency, gaps, and readiness across roles.",
        icon: Layers,
    },
    {
        title: "Adaptive Assessments",
        desc: "AI-driven adaptive testing that measures deep skill and prevents the drift of legacy MCQs.",
        icon: Cpu,
    },
    {
        title: "Outcome Analytics",
        desc: "Executive dashboards that connect skills, projects, and revenue-relevant KPIs.",
        icon: LineChart,
    },
];

const AIExperience = () => (
    <section data-testid="ai-experience-section" className="sxe-section relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
            <div className="hero-orb w-[600px] h-[600px] bg-[#A100FF]/15 top-10 -left-40" />
            <div className="hero-orb w-[500px] h-[500px] bg-[#7B00CC]/15 bottom-0 right-0" />
        </div>
        <div className="sxe-container relative">
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
                <div>
                    <div className="overline"><span className="dot" /> AI Powered Learning</div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] text-white mt-5">
                        Learning that <span className="text-gradient">thinks with you</span>.
                    </h2>
                    <p className="text-[#B0A3C4] text-lg leading-relaxed mt-5">
                        AI is not a bolt-on inside SpringXE — it&apos;s the operating substrate. Every module, path, assessment, and admin decision is augmented by intelligence trained on enterprise learning signals.
                    </p>

                    <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                        {[
                            { k: "Adaptive paths", v: "97%" },
                            { k: "Time saved", v: "42%" },
                            { k: "Coach hours", v: "12M+" },
                        ].map((s) => (
                            <div key={s.k} className="sxe-card p-4">
                                <div className="big-num text-3xl">{s.v}</div>
                                <div className="text-[11px] uppercase tracking-widest text-[#75668C] font-display mt-1">{s.k}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                    {cards.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.55, delay: i * 0.06 }}
                            className={`sxe-card p-6 ${i % 3 === 0 ? "sm:col-span-2" : ""}`}
                            data-testid={`ai-card-${i}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/[0.05] border border-[#C266FF]/25 flex items-center justify-center">
                                    <c.icon className="w-5 h-5 text-[#E9CCFF]" />
                                </div>
                                <ArrowUpRight className="w-4 h-4 text-[#75668C]" />
                            </div>
                            <h3 className="font-display text-white text-xl font-medium tracking-tight mt-5">{c.title}</h3>
                            <p className="text-[#B0A3C4] text-[14px] leading-relaxed mt-2">{c.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

export default AIExperience;
